import { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable';
import KanbanColumn from './KanbanColumn';
import TaskCard from './TaskCard';
import { STATUS_CONFIG } from '@config/constants';
import { useReorderTasks, useAllowedTransitions } from '@hooks';
import { useThemeStore } from '@store';

const DEFAULT_COLUMNS = ['backlog', 'todo', 'progress', 'review', 'done'];

export default function KanbanBoard({ tasks, taskStatuses, projectId, onTaskMove, onAddTask, onTaskClick, isLoading }) {
  const [activeTask, setActiveTask] = useState(null);
  const reorderTasks = useReorderTasks();
  const { isTransitionAllowed, hasRules } = useAllowedTransitions(projectId);
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  // Use dynamic statuses if provided, otherwise fall back to defaults
  const columns = useMemo(() => {
    if (taskStatuses && taskStatuses.length > 0) {
      return taskStatuses.map(s => ({
        id: s.name,
        label: s.label,
        color: s.color,
        bgColor: s.bgColor,
      }));
    }
    return DEFAULT_COLUMNS.map(status => ({
      id: status,
      label: STATUS_CONFIG[status]?.label || status,
      color: STATUS_CONFIG[status]?.color || '#64748b',
      bgColor: STATUS_CONFIG[status]?.bg || '#f1f5f9',
    }));
  }, [taskStatuses]);

  const columnIds = useMemo(() => columns.map(c => c.id), [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getTasksByStatus = (status) => {
    return tasks?.filter((task) => task.status === status).sort((a, b) => (a.order || 0) - (b.order || 0)) || [];
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const task = tasks?.find((t) => t.id === active.id);
    setActiveTask(task);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const draggedTask = tasks?.find((t) => t.id === active.id);
    if (!draggedTask) return;

    // Check if dropping on a column
    if (columnIds.includes(over.id)) {
      if (draggedTask.status !== over.id) {
        // Check if transition is allowed
        if (hasRules && !isTransitionAllowed(draggedTask.status, over.id)) {
          return; // Don't move if blocked
        }
        onTaskMove?.(active.id, over.id);
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const draggedTask = tasks?.find((t) => t.id === active.id);
    if (!draggedTask) return;

    // Check if dropped on another task (for reordering within column)
    const overTask = tasks?.find((t) => t.id === over.id);

    if (overTask && draggedTask.status === overTask.status && active.id !== over.id) {
      // Reordering within the same column
      const columnTasks = tasks
        .filter((t) => t.status === draggedTask.status)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

      const oldIndex = columnTasks.findIndex((t) => t.id === active.id);
      const newIndex = columnTasks.findIndex((t) => t.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        const reordered = arrayMove(columnTasks, oldIndex, newIndex);
        const updates = reordered.map((task, index) => ({
          id: task.id,
          order: index,
        }));

        reorderTasks.mutate({ tasks: updates });
      }
      return;
    }

    // Determine the target column for cross-column moves
    let targetStatus = over.id;
    if (!columnIds.includes(over.id)) {
      // Dropped on a task in a different column
      if (overTask) {
        targetStatus = overTask.status;
      }
    }

    if (draggedTask.status !== targetStatus) {
      // Validate transition before moving
      if (hasRules && !isTransitionAllowed(draggedTask.status, targetStatus)) {
        // Transition not allowed - do nothing
        return;
      }
      onTaskMove?.(active.id, targetStatus);
    }
  };

  if (isLoading) {
    const columnBgClass = isDark ? 'bg-slate-800/60' : 'bg-slate-50';
    const skeletonClass = isDark ? 'bg-slate-700' : 'bg-slate-200';

    return (
      <div className="flex gap-4 overflow-x-auto pb-4 px-6">
        {(columns.length > 0 ? columns : DEFAULT_COLUMNS.map(id => ({ id }))).map((col) => (
          <div
            key={col.id}
            className={`min-w-[280px] flex-shrink-0 ${columnBgClass} rounded-2xl p-4 animate-pulse`}
          >
            <div className={`h-6 ${skeletonClass} rounded w-24 mb-4`}></div>
            <div className="space-y-3">
              <div className={`h-24 ${skeletonClass} rounded-xl`}></div>
              <div className={`h-24 ${skeletonClass} rounded-xl`}></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex gap-4 overflow-x-auto pb-4 px-6">
        {columns.map((column) => {
          // Determine if this column is blocked for the current drag
          const isDropBlocked = activeTask && hasRules && !isTransitionAllowed(activeTask.status, column.id) && activeTask.status !== column.id;

          return (
            <KanbanColumn
              key={column.id}
              id={column.id}
              title={column.label}
              color={column.color}
              tasks={getTasksByStatus(column.id)}
              onAddTask={onAddTask}
              onTaskClick={onTaskClick}
              isDropDisabled={isDropBlocked}
            />
          );
        })}
      </div>

      <DragOverlay>
        {activeTask ? (
          <div className="rotate-3 scale-105">
            <TaskCard task={activeTask} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

KanbanBoard.propTypes = {
  tasks: PropTypes.array,
  taskStatuses: PropTypes.array,
  projectId: PropTypes.number,
  onTaskMove: PropTypes.func,
  onAddTask: PropTypes.func,
  onTaskClick: PropTypes.func,
  isLoading: PropTypes.bool,
};
